'use client'
import { getUserFavorites } from '@/api/favorites'
import { useGiphyFetch } from '@/app/util/use-gf'
import Grid from '@/components/grid/grid'
import { SectionHeader } from '@/components/sections'
import FavoritesContext from '@/context/favorites-manager'
import { Interface } from '@/styles/fonts'
import { useClientSideAPIKey } from '@/util/url'
import { giphyRed } from '@giphy/colors'
import { IGif } from '@giphy/js-types'
import { useContext } from 'react'
import styled from 'styled-components'
import EmptyStatePlaceholder from './placeholder'

const Container = styled.div`
    line-height: 1.4;
    margin-top: 20px;
`

const HeartIcon = styled.i`
    display: inline-block;
    margin: 0 1px;
    vertical-align: middle;
`

const LocalFavoritesBanner = styled.div`
    text-align: left;
    background: linear-gradient(-135deg, rgb(255, 102, 102) 0%, rgb(153, 51, 255) 100%);
    border-radius: 0px;
    padding: 12px;
    font-family: ${Interface.style.fontFamily};
    font-size: 15px;
    margin: 6px 0px;
    box-sizing: content-box;
    h5 {
        font-family: ${Interface.style.fontFamily};
        font-size: 17px;
        font-weight: bold;
        margin-bottom: 6px;
        color: rgb(255, 255, 255);
    }
    a {
        font-weight: bold;
        margin-top: 6px;
        display: inline-block;
    }
    .ss-icon {
        font-weight: normal;
        display: inline-block;
        font-size: 11px;
        height: 13px;
    }
`

const clientsideFetchLimit: number = 25

const NoFavorites = () => {
    return (
        <Container>
            <EmptyStatePlaceholder height={200} colors={['#1A1320', '#20171B']}>
                <div>
                    You don&apos;t have any <span style={{ color: giphyRed }}>favorites</span> yet!
                </div>
                <div>
                    Add to your favs by clicking the <HeartIcon className="ss-heart" />
                    around GIPHY.
                </div>
            </EmptyStatePlaceholder>
        </Container>
    )
}

export const useLocalFavorites = (limit: number) => {
    const { favorites } = useContext(FavoritesContext)
    const gf = useGiphyFetch()

    const fetchGifs = async (offset: number) => {
        const moreGifs = favorites?.slice(offset, offset + limit)
        if (!moreGifs || moreGifs.length === 0) {
            return {
                data: [],
                pagination: {
                    count: 0,
                    total_count: favorites?.length ?? 0,
                    offset,
                },
                meta: {
                    status: 200,
                    msg: 'OK',
                    response_id: 'local-favorites',
                },
            }
        }

        try {
            const result = await gf.gifs(moreGifs)
            result.pagination.total_count = favorites?.length ?? 0
            return result
        } catch (error) {
            console.error('Error fetching GIFs for local favorites:', error)
            return {
                data: [],
                pagination: { count: 0, total_count: favorites.length, offset },
                meta: { status: 500, msg: 'Error', response_id: 'local-favorites' },
            }
        }
    }

    return { favorites, fetchGifs }
}

const LocalFavoritesGrid = () => {
    const limit = 25
    const { favorites, fetchGifs } = useLocalFavorites(limit)
    return <>{favorites && favorites.length > 0 ? <Grid fetchGifs={fetchGifs} /> : <NoFavorites />}</>
}

type Props = { userToken?: string; initialGifs: IGif[] }
const Favorites = ({ userToken, initialGifs }: Props) => {
    const apiKey = useClientSideAPIKey()
    return (
        <>
            {userToken ? (
                <>
                    <SectionHeader label="My Favorites" className="my-2" />
                    {initialGifs.length > 0 ? (
                        <Grid
                            initialGifs={initialGifs}
                            fetchGifs={async (offset: number) => {
                                return await getUserFavorites(
                                    { api_key: apiKey, limit: clientsideFetchLimit, offset },
                                    userToken
                                )
                            }}
                        />
                    ) : (
                        <NoFavorites />
                    )}
                </>
            ) : (
                <>
                    <LocalFavoritesBanner className={Interface.className}>
                        <h5>Create a GIPHY account to access your favs from anywhere!</h5>
                        <p>
                            Your favorites are safer with a GIPHY account, these are only saved locally on this phone &
                            could be lost.
                        </p>
                        <a href="/login">
                            Login or Sign Up <div className="ss-icon ss-navigateright" />
                        </a>
                    </LocalFavoritesBanner>
                    <SectionHeader label="My Favorites" />
                    <LocalFavoritesGrid />
                </>
            )}
        </>
    )
}

export default Favorites
